import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Shuffle, Pin, X } from "lucide-react";

const MONTHS = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "June", "July", "Aug.", "Sept.", "Oct.", "Nov.", "Dec."];
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export function longDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DAYS[dt.getDay()]}, ${MONTHS[m - 1]} ${d}, ${y}`;
}

const shift = (month, n) => {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const label = (month) => new Date(`${month}-01T00:00:00Z`).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

function Picker({ day, onClose, onPicked }) {
  const [search, setSearch] = useState("");
  const { data } = useQuery({ queryKey: ["admin-questions", "daily-picker", search], queryFn: async () => (await api.get("/admin/questions", { params: { search: search || undefined, limit: 50 } })).data });
  return (
    <Dialog open={!!day} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-lg overflow-hidden" data-testid="daily-picker">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Pick the Daily Spark for {longDate(day?.date)}</DialogTitle>
          <DialogDescription>Choose any question — or let it stay random for the day.</DialogDescription>
        </DialogHeader>
        <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search questions…" data-testid="daily-picker-search" />
        <ul className="max-h-[45vh] divide-y divide-border overflow-y-auto rounded-xl border border-border">
          {data?.questions.map((q) => (
            <li key={q.id}>
              <button type="button" onClick={() => onPicked(q)} data-testid={`daily-pick-${q.id}`} className="w-full px-3 py-2 text-left transition-colors hover:bg-muted">
                <p className="truncate font-serif text-base text-foreground">&ldquo;{q.question_kjv}&rdquo;</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{q.reference} · {q.who_asked.join(", ")}</p>
              </button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDaily() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [picking, setPicking] = useState(null);
  const { data } = useQuery({ queryKey: ["admin-daily", month], queryFn: async () => (await api.get("/admin/daily", { params: { month } })).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-daily"] }); qc.invalidateQueries({ queryKey: ["admin-overview"] }); qc.invalidateQueries({ queryKey: ["hero-daily"] }); qc.invalidateQueries({ queryKey: ["daily"] }); };

  const pin = async (date, question_id) => {
    try { await api.put(`/admin/daily/${date}`, { question_id }); toast.success(`Daily Spark set for ${date}.`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clear = async (date) => {
    try { await api.delete(`/admin/daily/${date}`); toast.success(`${date} is back to random.`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const randomize = async (date) => {
    const r = (await api.get("/questions/daily", { params: { shuffle: true, exclude: data?.days.find((d) => d.date === date)?.question?.id } })).data;
    pin(date, r.id);
  };

  const firstDow = data ? new Date(`${month}-01T00:00:00Z`).getUTCDay() : 0;

  return (
    <div className="space-y-4" data-testid="admin-daily">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonth(shift(month, -1))} data-testid="daily-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <h2 className="min-w-[12rem] text-center font-serif text-2xl font-semibold text-foreground" data-testid="daily-month-label">{label(month)}</h2>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonth(shift(month, 1))} data-testid="daily-next-month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <p className="text-sm text-muted-foreground">Gold = hand-picked · grey = random for that day. Click a day to choose.</p>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2" data-testid="daily-grid">
        {Array.from({ length: firstDow }).map((_, i) => <span key={`pad-${i}`} />)}
        {data?.days.map((d) => {
          const scheduled = d.source === "scheduled";
          const today = d.date === data.today;
          return (
            <div
              key={d.date}
              data-testid={`daily-day-${d.date}`}
              data-source={d.source}
              className={`group relative flex min-h-[5.5rem] flex-col rounded-xl border p-1.5 text-left transition-colors sm:min-h-[7rem] sm:p-2 ${
                scheduled ? "border-gold bg-gold/10" : "border-border bg-card"
              } ${today ? "ring-2 ring-primary/50" : ""}`}
            >
              <button type="button" onClick={() => setPicking(d)} className="flex flex-1 flex-col text-left" data-testid={`daily-open-${d.date}`}>
                <span className={`font-mono text-xs ${today ? "font-bold text-primary" : "text-muted-foreground"}`}>{Number(d.date.slice(-2))}</span>
                {d.question && (
                  <span className="mt-1 line-clamp-3 font-serif text-[11px] leading-snug text-foreground sm:text-xs">&ldquo;{d.question.question_kjv}&rdquo;</span>
                )}
                <span className="mt-auto hidden font-mono text-[9px] uppercase tracking-wider text-muted-foreground sm:block">{d.question?.reference}</span>
              </button>
              <div className="absolute right-1 top-1 hidden gap-0.5 group-hover:flex">
                <button type="button" title="Random pick" onClick={() => randomize(d.date)} className="rounded-full bg-background/90 p-1 text-muted-foreground hover:text-foreground" data-testid={`daily-random-${d.date}`}><Shuffle className="h-3 w-3" /></button>
                {scheduled ? (
                  <button type="button" title="Back to random" onClick={() => clear(d.date)} className="rounded-full bg-background/90 p-1 text-muted-foreground hover:text-destructive" data-testid={`daily-clear-${d.date}`}><X className="h-3 w-3" /></button>
                ) : (
                  <Pin className="h-5 w-5 rounded-full bg-background/90 p-1 text-muted-foreground" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Picker day={picking} onClose={() => setPicking(null)} onPicked={(q) => { pin(picking.date, q.id); setPicking(null); }} />
    </div>
  );
}
